import firebase from 'firebase/app';

import TrackerService from '../utils/TrackerService';

function getCountryCode(country) {
  switch (country) {
    case 'UK':
      return 'GB';
    default:
      return country;
  }
}

/**
 * Infer status for historical payout objects that don't have a status field.
 * Historical payouts relied on the `hide` field to determine visibility.
 * This function adds a status field based on the hide value and account type.
 */
function inferStatusForHistoricalPayout(payout) {
  // If status already exists, don't override it
  if (payout.status !== undefined && payout.status !== null) {
    return payout.status;
  }

  const { hide, provider, type } = payout;
  
  // If hidden, treat as paused (legacy behavior)
  if (hide === true) {
    return 'paused';
  }
  
  // For classic accounts (no provider/type), use 'ready' status
  // For provider-managed accounts, use 'live' if they were not hidden
  if (!provider && !type) {
    return 'ready';
  } else {
    // Provider-managed accounts that weren't hidden were likely active
    return 'live';
  }
}

export default class BankAccountsModel {
  constructor({ viewModel, clientId, filter }) {
    this.viewModel = viewModel;
    this.clientId = clientId;
    this.payouts = [];
    this.client = null;
    this.payoutsById = {};
    this.filter = filter;
  }

  getQueryForPayoutsForClient(params) {
    const {
      clientId,
    } = params;

    const db = firebase.firestore();
    return db.collection('clients').doc(clientId).collection('payouts');
  }

  stop() {
    // stop all listeners.,.not sure they get dropped when object drops out of scope.
    if(this.observerPayouts) {
      this.observerPayouts();
      this.observerPayouts = null;
    }
  }

  async fetchAllPayoutsData(isAdmin)  {
    if (this.clientId === null) return;
    const clientId = this.clientId;
    this.stop();
    this.payouts = [];

    const query = this.getQueryForPayoutsForClient({
      clientId,
    });

    this.observerPayouts = query.onSnapshot(querySnapshot => {
      let index = querySnapshot.size;
      this.payouts = [];
      if(index === 0) this.updateDataSource();
      querySnapshot.docChanges().forEach(
        change => {
          if (change.type === 'added' || change.type === 'modified') {
            const payout = change.doc.data();
            const id = change.doc.id;
            const full_payout = { ...payout, id };
            const { hide, country, address } = payout;
            const { country: addrCountry } = address || {};
            
            // Infer status for historical payouts that don't have a status field
            // This must happen BEFORE filtering, so the filter can check status correctly
            full_payout.status = inferStatusForHistoricalPayout(full_payout);
            
            if (country) {
              full_payout.country = getCountryCode(country);
            }
            if (address && addrCountry) {
              full_payout.address.country = getCountryCode(addrCountry);
            }
            // Include accounts for admin users, non-hidden accounts, or retry accounts with errors (user needs to fix them)
            let isIncluded = (isAdmin || !hide || (full_payout.status === 'retry' && full_payout.severity === 'error'));
            if (this.filter) {
              isIncluded = this.filter(full_payout, isAdmin);
            }
            if (isIncluded) {
              this.payoutsById[id] = full_payout;
            } else {
              // Ensure previously-included entries are removed when they no longer match the filter (e.g., hidden)
              delete this.payoutsById[id];
            }
            this.updateDataSource();
          }

          if (change.type === 'removed' )  {
            const id = change.doc.id;
            delete this.payoutsById[id];
            this.updateDataSource();
          }
        }
      );
    }, err => {
      this.payouts = [];
      this.payoutsById = {};
      this.updateDataSource();
      console.log(`Payout query error: ${err}`);
    });


  }

  async updateDataSource() {

    this.payouts = [];

    for(let prop in this.payoutsById) {
      this.payouts.push(this.payoutsById[prop]);  
    }

    this.viewModel.updatedPayoutsDataSource(this.payouts);
  }

  assignProcessorToPayout(payout) {
    const { country, currency, type, provider } = payout;
    const adjustedPayout = { ...payout };

    // Preserve MoneyJar accounts (type='instant', provider='moneyjar') - they can be any currency
    if (type === 'instant' && provider === 'moneyjar') {
      return adjustedPayout;
    }
    
    // Set provider/type for USD/US accounts only
    // Form ensures country and currency are always set, so no need for defensive clearing
    if (currency === 'USD' && country === 'US') {
      adjustedPayout.provider = 'stripeus';
      adjustedPayout.type = 'auto';
    }
    
    return adjustedPayout;
  }

  async makeNewPayoutObject(payoutData) {
    if (this.clientId === null) return;
    const clientId = this.clientId;
    const query = this.getQueryForPayoutsForClient({
      clientId,
    });

    const data = this.assignProcessorToPayout(payoutData);
    const payout = await query.add(data);
    const payoutId = payout.id;
    const isLocalHost = window.location.hostname === 'localhost';
    const isTest = isLocalHost;
    const { 
      source, 
      currency, bic, sortCode, routingNumber, isCompany, 
      provider, type, status,
      descriptor,
    } = data;

    const tracker = new TrackerService({
      isTest,
    });

    const analyticsData = {
      clientId,
      payoutId,
      currency,
      source,
      bic, 
      sortCode, 
      routingNumber, 
      isCompany,
      provider, 
      type, 
      status,
      descriptor,
    };

    // console.log(`analyticsData: ${JSON.stringify(analyticsData)}`);

    tracker.mixpanelTrack("payout_created", {
      distinct_id: clientId,
      ...analyticsData,
    });

    if (!isTest) {
      tracker.googleTrack("payout_created", analyticsData);
    }

    return payoutId;
  }

  async updateExistingPayout(id, data) {
    if (this.clientId === null) return;
    const clientId = this.clientId;
    const query = this.getQueryForPayoutsForClient({
      clientId,
    });
    // When updating an existing payout, we need to check on status
    // If status is `retry`
    // We need to re-start the payout from `ready` status
    // This will re-try the createStripeBankAccount process
    const payout = await this.getPayout(clientId, id);
    const existingStatus = payout?.status;
    
    // If status is 'retry', reset to 'ready' to retry the process
    if (existingStatus === 'retry') {
      data.status = 'ready';
    }
    // If status doesn't exist in existing payout and not provided in data, infer it
    // This matches the logic in inferStatusForHistoricalPayout helper function
    else if ((existingStatus === undefined || existingStatus === null) && (data.status === undefined || data.status === null)) {
      // Merge existing payout with incoming data to get full context for inference
      const mergedPayout = { ...payout, ...data };
      // Use the same helper function to infer status consistently
      data.status = inferStatusForHistoricalPayout(mergedPayout);
    }
    
    // IMPORTANT: Also process the data through assignProcessorToPayout to ensure
    // provider/type are correctly set/cleared based on currency
    const processedData = this.assignProcessorToPayout(data);
    await query.doc(id).set(processedData, { merge : true });
  }

  async markPayoutHidden(id, hide) {
    if (this.clientId === null) return;
    const clientId = this.clientId;
    const query = this.getQueryForPayoutsForClient({
      clientId,
    });

    const updateData = {
      hide,
      updatedAt: firebase.firestore.Timestamp.now(),
    };

    // When marking as deleted (hide = true), set status to 'deleted'
    if (hide) {
      updateData.status = 'deleted';
      await query.doc(id).set(updateData, { merge : true });
      return;
    }

    // When restoring (hide = false):
    // - For classic accounts (no provider/type), set status back to 'ready' so it appears Active
    // - For provider-managed accounts, reset status from 'deleted' to 'ready' to make them active again
    const existing = await this.getPayout(clientId, id);
    const isClassic = existing && !existing.provider && !existing.type;
    if (isClassic) {
      updateData.status = 'ready';
    } else if (existing && existing.status === 'deleted') {
      // For provider-managed accounts that were deleted, restore them to 'ready' status
      updateData.status = 'ready';
    }

    await query.doc(id).set(updateData, { merge : true });
  }

  async getPayout(clientId, payoutId) {
    const db = firebase.firestore();

    if (payoutId && clientId) {
      const payoutSnapshot = await
        db
          .collection('clients')
          .doc(clientId)
          .collection('payouts')
          .doc(payoutId)
          .get();

      if (payoutSnapshot.exists) {
        const payoutData = payoutSnapshot.data();
        return payoutData;
      }
    }
    return null;
  }
}
